import React, { useState } from 'react'
import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  ButtonGroup,
  VStack,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  Tooltip,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Card from 'components/card/Card'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlinePlus, AiOutlineSearch } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { MdPlace, MdLocalParking, MdAirlineSeatReclineNormal } from 'react-icons/md'
import { useGetVenues } from 'queries/venue.query'
import { CreateVenueModal } from './CreateVenueModal'
import { EditVenueModal } from './EditVenueModal'
import { DeleteVenueModal } from './DeleteVenueModal'
import { useNavigate } from 'react-router-dom'

const columnHelper = createColumnHelper()

export default function VenueTable() {
  const [search, setSearch] = useState('')
  const [selectedVenue, setSelectedVenue] = useState(null)
  const navigate = useNavigate()

  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')

  const { isOpen: isCreateOpen, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()

  const { data, isLoading } = useGetVenues(1, 50, search)

  const venuesList = React.useMemo(() => {
    if (data?.data) return data.data
    if (Array.isArray(data)) return data
    return []
  }, [data])

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Venue',
        cell: (info) => (
          <VStack align="start" spacing={1}>
            <Text color={textColor} fontWeight="700" fontSize="sm">
              {info.getValue()}
            </Text>
            <Flex align="center" gap={1} color="gray.500" fontSize="xs">
              <MdPlace />
              <Text>{info.row.original.city}, {info.row.original.country}</Text>
            </Flex>
          </VStack>
        ),
      }),
      columnHelper.accessor('address', {
        header: 'Address',
        cell: (info) => (
          <Text color={textColor} fontSize="sm">
            {info.getValue()} {info.row.original.postal_code}
          </Text>
        ),
      }),
      columnHelper.accessor('total_capacity', {
        header: 'Capacity',
        cell: (info) => (
          <Flex align="center" gap={1}>
            <MdAirlineSeatReclineNormal color="#7551FF" />
            <Text color={textColor} fontWeight="600" fontSize="sm">
              {info.getValue() ? Number(info.getValue()).toLocaleString() : 'N/A'}
            </Text>
          </Flex>
        ),
      }),
      columnHelper.accessor('parking_info', {
        header: 'Parking',
        cell: (info) => {
          const val = info.getValue()
          if (!val) {
            return <Tag size="sm" colorScheme="gray">No parking</Tag>
          }
          return (
            <Tooltip label={val} placement="top">
              <Tag size="sm" colorScheme="green" display="flex" gap={1} cursor="pointer">
                <MdLocalParking />
                <Text isTruncated maxW="180px">Dedicated Parking</Text>
              </Tag>
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor('seating_plans_count', {
        header: 'Seating Plans',
        cell: (info) => (
          <Button
            size="xs"
            colorScheme="brand"
            variant="outline"
            onClick={() => navigate(`/admin/seating-plans?venue_id=${info.row.original.id}`)}
          >
            View Plans ({info.row.original.seating_plans_count || 0})
          </Button>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme="brand"
              onClick={() => {
                setSelectedVenue(info.row.original)
                onOpenEdit()
              }}
            >
              <EditIcon />
            </Button>
            <Button
              colorScheme="red"
              onClick={() => {
                setSelectedVenue(info.row.original)
                onOpenDelete()
              }}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        ),
      }),
    ],
    [textColor, navigate, onOpenEdit, onOpenDelete]
  )

  const table = useReactTable({
    data: venuesList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (isLoading) return <SkeletonTable />

  return (
    <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex px="25px" mb="20px" justifyContent="space-between" align="center" flexWrap="wrap" gap={3}>
        <VStack align="start" spacing={0}>
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Event Venues & Arenas
          </Text>
          <Text color="gray.400" fontSize="sm" mt={1}>
            Manage concert halls, stadiums, and cultural venue configurations across Germany & Europe
          </Text>
        </VStack>

        <Flex gap={3} align="center">
          <InputGroup size="sm" maxW="250px">
            <InputLeftElement pointerEvents="none">
              <AiOutlineSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search venue or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="12px"
            />
          </InputGroup>

          <Button
            leftIcon={<AiOutlinePlus />}
            colorScheme="brand"
            size="sm"
            padding="10px 20px"
            borderRadius="12px"
            onClick={onOpenCreate}
          >
            New Venue
          </Button>
        </Flex>
      </Flex>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead bg={TheadBackground}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    borderColor={borderColor}
                    py="12px"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={8}>
                  No venues found. Click "Add Venue" to create one.
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} borderColor={borderColor} py="14px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <CreateVenueModal isOpen={isCreateOpen} onClose={onCloseCreate} />
      <EditVenueModal isOpen={isEditOpen} onClose={onCloseEdit} venue={selectedVenue} />
      <DeleteVenueModal isOpen={isDeleteOpen} onClose={onCloseDelete} venue={selectedVenue} />
    </Card>
  )
}
